import { createContext, useState, useContext, useLayoutEffect } from 'react'
import { isAvatarSrc, thumbnailMediumBlue } from '@utils/thumbnails'
import useCurrentLocationContext from './currentLocationContext'
import profileMarker from '@contexts/map_context/profileMarker'
import { useAuthContext } from './authContext'
import { useLocation } from 'react-router-dom'
import useStateRef from 'react-usestateref'
import useLoading from '@hooks/useLoading'
import shuffleArr from '@utils/shuffleArr'
import axios from '@utils/webRequests'
import distance from '@turf/distance'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import * as d3 from 'd3'



// ******************
// * MAP DATA CLASS *
// ******************
class MapDataClass {
  static MAPBOX_STYLE_URL = 'mapbox://styles/keyboard-warrior/clsbdanzt00hk01po4m96ajri'
  static PROFILE_RADIUS = 36
  static INITIAL_ZOOM = 13.5
  static MAX_ZOOM = 18
  MAPBOX_STYLE_URL = MapDataClass.MAPBOX_STYLE_URL
  PROFILE_RADIUS = MapDataClass.USER_PROFILE_RADIUS
  INITIAL_ZOOM = MapDataClass.INITIAL_ZOOM
  MAX_ZOOM = MapDataClass.MAX_ZOOM
  mapDataIsInitialized = false
  profilesInitialized = false
  mapEventHandlesInitialized = false
  AnimatedLonlatDatum = {}
  D3Container = {}
  D3Selection = {}
  profiles = {}
  map = {}
  mapContainer = {}
  currentVisibility = { value: true }
  navigateFunc = { value: null }
  lastZoom = { value: this.INITIAL_ZOOM }
  forceAnimationTimeout = { value: null }
  focusedUser = { }
  ERRORS = {
    PROFILE_NOT_FOUND: 'PROFILE_NOT_FOUND',
    USER_INVALID: 'USER_INVALID',
    COORDINATE_INVALID: 'COORDINATE_INVALID',
  }
  errorCallbacks = {
    [this.ERRORS.PROFILE_NOT_FOUND]: () => console.error(`ERROR: User cannot be found in map profiles`),
    [this.ERRORS.USER_INVALID]: () => console.error(`ERROR: The user input to mapDataContext is not valid`),
    [this.ERRORS.COORDINATE_INVALID]: () => console.error(`ERROR: a location was not provided, or was the wrong format`),
  }

  constructor() {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN
  }

  LonlatDatum = class {
    constructor({ lonlat, id }) {
      // lonlat: { lng: number, lat: number }
      this.lonlat = lonlat
      this.id = id
      this.r = MapDataClass.PROFILE_RADIUS
    }
  }

  XYDatum = class {
    constructor({ x, y, id }) {
      this.x = x
      this.y = y
      this.id = id
      this.r = MapDataClass.PROFILE_RADIUS
    }
  }

  ProfileMarker = class {
    constructor({ MD, user, hasEntranceAnimation=true }) {
      this.MD = MD
      this.user = user
      this.isAvatar = isAvatarSrc('medium', user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
      this.profileImage = thumbnailMediumBlue(user?.nearbyMedia?.thumbnails?.[0], user?.defaultAvatar)
      this.options = {
        isCurrentUser: user?.isCurrentUser,
        profileImage: this.profileImage,
        isFocused: user?.isFocused,
        isAvatar: this.isAvatar,
        hasEntranceAnimation,
      }
      this.element = profileMarker(user, this.options)
      this.datum = null
      this.selection = null
    }

    createXYDatum = () => {
      this.datum = this.MD.getXyDatumFromUser(this.user)
      return this
    }

    createSelection = () => {
      this.selection = this.MD.getSelection(this.user)
      return this
    }

    updateD3SelectionDatum = (valObj={}) => {
      const { createSelection, createXYDatum } = this
      if (!this.datum) createXYDatum()
      if (!this.selection) createSelection()
      for (let key in valObj) { key in this.datum && (this.datum[key] = valObj[key]) }
      this.selection.node(this.datum)
      return this
    }

    addToD3Container = () => {
      const { MD, createXYDatum } = this
      // parse datum from the user data
      if (!this.datum) createXYDatum()
      // Append the new foreign object with new data to the existing nodes
      MD.D3Container
        .append('foreignObject')
        .datum(this.datum)
        .attr("x", d => d.x)
        .attr("y", d => d.y)
        .attr("r", d => d.r)
        .attr("id", d => d.id)
        .attr("height", 1)
        .attr("width", 1)
        .attr("overflow", 'visible')

      return this
    }

    removerFromD3Container = () => {
      const { MD, createSelection } = this
      if (!this.selection) createSelection()
      this.selection.remove()
      return this
    }

    addToD3Selection = () => {
      const { MD, element, createSelection } = this
      if (!this.selection) createSelection()
      let node = this.selection.node()
      node.innerHTML = ''
      node.append(element)
      // update the existing D3Selection reference to the nodes within the container
      MD.updateD3Selection()
      return this
    }

    addToD3 = () => {
      this.addToD3Container()
      this.addToD3Selection()
      return this
    }

    addToD3LoweredOrRaised = () => {
      const { addToD3Container, addToD3Selection, user, isAvatar } = this
      addToD3Container().createSelection()
      // Send profile to bottom in stack order if user picture is an avatar (display under overlapping users)
      if (isAvatar) this.selection.lower()
        // Raise profile to the top of the stack order if user is subscribed (display over overlapping users)
      if (user.isSubscribed || user.isCurrentUser) this.selection.raise()
      addToD3Selection()
      return this
    }
  }

  initializeMap = (lonlat, mapContainer) => {
    const { MAX_ZOOM, INITIAL_ZOOM, MAPBOX_STYLE_URL } = this
    this.mapContainer = mapContainer
    this.map = new mapboxgl.Map({
      container: mapContainer,
      // style: 'mapbox://styles/keyboard-warrior/clsb581ea00hb01po1pwk7rag', // indigoish
      style: MAPBOX_STYLE_URL,
      center: lonlat,
      dragRotate: false,
      pitchWithRotate: false,
      touchPitch: false,
      // NOTE: We need to disable resizing so the map doesn't glitch when the keyboard appears on the screen
      // Unfortunately that means resizing the page will be a bad experience, but that shouldn't be a serious issue on mobile.
      trackResize: false,
      maxZoom: MAX_ZOOM,
      zoom: INITIAL_ZOOM,
    })
    return this.map
  }

  initializeD3Container = () => {
    this.D3Container = d3
      .select(this.map.getCanvasContainer())
      .append("svg")
      .attr('id', 'node-group')
      .attr("width", "100%")
      .attr("height", "100%")
      .style("position", "absolute")
      .style('top', 0)
      .style('left', 0)
      .style("z-index", 2)
  }

  updateD3Selection = () => {
    this.D3Selection = this.D3Container.selectAll('foreignObject')
  }

  setMapDataIsInitialized = (trueOrFalse) => {
    this.mapDataIsInitialized = trueOrFalse
  }

  setProfilesInitialized = (trueOrFalse) => {
    this.profilesInitialized = trueOrFalse
  }

  setMapEventHandlesInitialized = (trueOrFalse) => {
    this.mapEventHandlesInitialized = trueOrFalse
  }

  renderError = (errorType) => this.errorCallbacks[errorType]()

  importProfiles = (newProfiles) => this.profiles = newProfiles;

  getSelection = (user) => this.D3Container.select(`foreignObject#${user.nearbyUuid}`)

  getNode = (user) => this.D3Container.select(`foreignObject#${user.nearbyUuid}`).node()

  getProfileMarkerEl = (user) => {
    const node = this.getNode(user)
    return node ? node.querySelector('.profile-marker') : null
  }

  getProfile = (nearbyUuid) => {
    return this.profiles[nearbyUuid]
  }

  getProfiles = () => this.profiles;

  setProfile = (user, selection) => {
    selection ||= this.getSelection(user)
    user.selection = selection
    this.profiles[user?.nearbyUuid] = user
  }

  setFocusedUser = (user) => {
    this.focusedUser = user
  }

  count = () => this.profiles.keys().length;

  updateAnimatedLonlatDatumToBaseVal = () => {
    const { D3Selection, map, LonlatDatum } = this
    this.AnimatedLonlatDatum = D3Selection.nodes().map(({ x, y, id }) => new LonlatDatum({
      lonlat: map.unproject(({ x: x.baseVal.value, y: y.baseVal.value })),
      id,
    }))
  }
  
  updateAnimatedLonlatDatumToAnimVal = () => {
    const { D3Selection, map, LonlatDatum } = this
    this.AnimatedLonlatDatum = D3Selection.nodes().map(({ x, y, id }) => new LonlatDatum({
      lonlat: map.unproject(({ x: x.animVal.value, y: y.animVal.value })),
      id,
    }))
  }

  getXyDatumFromUser = (user) => {
    const { XYDatum, map } = this
    return new XYDatum({
      id: user.nearbyUuid,
      ...map.project(user.lonlat)
    })
  }

  getXyDatumFromUsers = (users) => (
    users.map((user) => this.getXyDatumFromUser(user))
  )

  // This sets the x & y positions of the markers on the map
  // to the new x/y values of the updated datum
  updateD3PositionsToDatum = () => {
    this.D3Selection
      .attr("x", d => d.x)
      .attr("y", d => d.y)
  }

  withinSameArea = (lonlat1, lonlat2) => {
    if (lonlat1 && lonlat2) {
      return distance(lonlat1, lonlat2, { units: 'feet' }) < 150
    } else {
      this.renderError(this.ERRORS.COORDINATE_INVALID)
    }
  }

  movePointCloser = (point1, point2) => {
    const deltaX = point2[0] - point1[0];
    const deltaY = point2[1] - point1[1];
    
    // how you manipulate the distance between the two points
    const coeff = 0.05; // this coefficient can be tweaked to decice how much near the two points will be after the update.. 0.5 = 50% of the previous distance
    
    return [point1[0] + (coeff * deltaX), point1[1] + (coeff * deltaY)]
  }

  mercatorXfromLng(lng) {
    return (180 + lng) / 360;
  }

  mercatorYfromLat(lat) {
    return (180 - (180 / Math.PI * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360)))) / 360;
  }
}



// ******************
// * MAP VIEW CLASS *
// ******************
class MapViewClass {
  constructor(MapData) {
    this.MD = MapData
  }

  // adds a single user to the map
  addFromData = (userData, { recalcDatum=true }) => {
    const { MD } = this
    // Don't run if the user is already present, or if the D3Selection doesn't exist
    if (MD.getProfile(userData) || !MD.D3Selection) return;
    // Don't run if the user does not have a location for whatever reason
    if (!userData.lonlat) return;
    // Create the new profile marker and append it to the newly created foreign object
    const newMarker = new MD.ProfileMarker({ MD, user: userData }).addToD3()
    MD.setProfile(userData, newMarker.selection)

    // reset the AnimatedLonlatDatum so that it can be re-calculated on the next event
    if (recalcDatum) {
      MD.updateAnimatedLonlatDatumToAnimVal()
    }
  }

  // Adds many users to the map
  addManyFromData = (userDataArr) => {
    const { MD, addFromData } = this
    userDataArr.forEach((userData) => {
      addFromData(userData, { recalcDatum: false })
    })
    MD.updateAnimatedLonlatDatumToAnimVal()
  }

  // sends a profile to the top of the d3 stack
  raise = (nearbyUuid) => {
    const { MD } = this
    if (MD.getProfile(nearbyUuid)) {
      MD.getProfile(nearbyUuid)?.selection?.raise()
    } else {
      MD.renderError(MD.ERRORS.PROFILE_NOT_FOUND)
    }
  }

  // Updates a user on the map with new data
  updateUser = (nearbyUuid, newUserData, options={ resetLocation: false }) => {
    const { MD, _updateUserWithoutLocationChange, _updateUserAndChangeLocation } = this
    if (!MD.profilesInitialized) return;
    const user = MD.getProfile(nearbyUuid)
    if (!user) return;
    
    const newMarkerData = { MD, user: { ...user, ...newUserData }, hasEntranceAnimation: false }

    if (options?.resetLocation) {
      // if we need to reset the users location, updated data to original location and replace there
      newMarkerData.lonlat = MD.map?.project(user?.lonlat)
      _updateUserAndChangeLocation(user, newMarkerData)

    } else if (MD.withinSameArea(user.lonlat, newMarkerData.user.lonlat)) {
      // If the user hasn't changed location significantly, keep their old location
      _updateUserWithoutLocationChange(user, newMarkerData)

    } else {
      // If the user has moved significantly, replace their marker to new location
      _updateUserAndChangeLocation(user, newMarkerData)
    }
  }

  _updateUserWithoutLocationChange = (user, newMarkerData) => {
    const { MD } = this
    newMarkerData.user.lonlat = user.lonlat
    // Create the new marker with new data, then update the datum in the node and replace the element
    const newMarker = new MD.ProfileMarker(newMarkerData)
      .updateD3SelectionDatum()
      .addToD3Selection()

    // Recalculate the AnimatedLonlatDatum with new vals, and set new user to profiles
    MD.updateAnimatedLonlatDatumToAnimVal()
    MD.setProfile(newMarker.user, newMarker.selection)
  }

  _updateUserAndChangeLocation = (user, newMarkerData) => {
    const { MD, removalAnimation } = this
    // Create the new marker with new data, then REPLACE the marker so the position updates correctly on map
    newMarkerData.hasEntranceAnimation = true
    return removalAnimation(user).then(() => {
      const newMarker = new MD.ProfileMarker(newMarkerData)
        .removerFromD3Container()
        .addToD3LoweredOrRaised()

      // Recalculate the AnimatedLonlatDatum with new vals, and set new user to profiles
      MD.updateAnimatedLonlatDatumToAnimVal()
      MD.setProfile(newMarker.user, newMarker.selection)
    })
  }

  // Removes a single profile from the map
  removeUser = (nearbyUuid, withAnimation=true, recalcAnimatedLonlatDatum=true) => {
    const { MD, deleteSelectionAndProfile, animatedDeleteSelectionAndProfile } = this
    const user = MD.getProfile(nearbyUuid)
    if (!user) return MD.renderError(MD.ERRORS.PROFILE_NOT_FOUND)
    withAnimation
      ? animatedDeleteSelectionAndProfile(user, recalcAnimatedLonlatDatum)
      : deleteSelectionAndProfile(user, recalcAnimatedLonlatDatum)
  }

  deleteSelectionAndProfile = (user, recalcAnimatedLonlatDatum) => {
    const { MD } = this
    user.selection.remove()
    delete MD.profiles[user.nearbyUuid]
    if (recalcAnimatedLonlatDatum) {
      MD.updateAnimatedLonlatDatumToAnimVal()
    }
  }

  animatedDeleteSelectionAndProfile = (user, recalcAnimatedLonlatDatum) => {
    const { removalAnimation, deleteSelectionAndProfile } = this
    removalAnimation(user).then(() => {
      deleteSelectionAndProfile(user, recalcAnimatedLonlatDatum)
    })
  }

  // Removes all profiles from the map
  removeAllUsers = () => {
    const { MD, removeUser } = this
    Object.keys(MD.profiles).forEach((nearbyUuid) => {
      removeUser(nearbyUuid, false, false)
    })
    MD.updateAnimatedLonlatDatumToAnimVal()
  }

  removalAnimation = (user) => {
    const { MD } = this
    return new Promise((resolve) => {
      const profileEl = MD.getProfileMarkerEl(user)
      if (profileEl) {
        profileEl.style.animation = 'growOutCenteredXY ease-in 300ms'
        profileEl.style.animationFillMode = 'forwards'
        setTimeout(resolve, 400)
      } else {
        resolve()
      }
    })
  }

  // Causes profile to pulse animate (for user activity)
  userActivity = (nearbyUuid) => {
    const { MD, raise } = this
    const user = MD.getProfile(nearbyUuid)
    if (user) {
      raise(nearbyUuid)
      const profileEl = user.selection.node().querySelector('.profile-marker')
      profileEl.style.animation = 'pulseCenteredXY ease-in-out 600ms'
      
      // Remove animation after
      setTimeout(() => {
        profileEl.style.animation = ''
      }, 700)
    } else {
      MD.renderError(MD.ERRORS.PROFILE_NOT_FOUND)
    }
  }

  // Toggles all profile markers visible and hidden
  setUserVisibility = (visible=true) => {
    const { MD } = this
    MD.currentVisibility.value = visible
    MD.D3Container.node().style.visibility = visible ? 'visible' : 'hidden'
  }

  focusOnUser = (user) => {
    const { MD, unFocusOnUser, updateUser, raise } = this
    const foundUser = MD.getProfile(user?.nearbyUuid)
    if (foundUser) {
      unFocusOnUser()
      updateUser(foundUser.nearbyUuid, { isFocused: true }, { resetLocation: true })
      raise(foundUser.nearbyUuid)
      MD.setFocusedUser(foundUser)
    }
  }

  unFocusOnUser = () => {
    const { MD, updateUser } = this
    if (MD.focusedUser?.nearbyUuid) {
      updateUser(MD.focusedUser?.nearbyUuid, { isFocused: false })
      MD.focusedUser = {}
    }
  }

  setUserHasNewReply = (nearbyUuid) => {
    this.updateUser(nearbyUuid, { lastMessage: { isNew: true, nearbyUuid: nearbyUuid } })
  }

  setUserHasNewSeenReply = (nearbyUuid) => {
    this.updateUser(nearbyUuid, { lastMessage: { isNew: false, nearbyUuid: nearbyUuid } })
  }

  setUserHasReadReply = (nearbyUuid) => {
    const otherUser = this.MD.getProfile(nearbyUuid)
    // Only update the read status if the user is found, and if the message isn't already read
    if (otherUser?.lastMessage && otherUser?.lastMessage?.isNew) {
      this.updateUser(nearbyUuid, { lastMessage: { ...otherUser.lastMessage, isNew: false } })
    }
  }

  setUserHasSentMessage = (nearbyUuid, currentUserUuid) => {
    this.updateUser(nearbyUuid, { lastMessage: { isNew: true, nearbyUuid: currentUserUuid } })
  }

  // applyFilters = (filters, nearbyUuids) => {
  //   // TODO: This will be its own feature down the line
  // }
}



// ************************
// * MAP CONTROLLER CLASS *
// ************************
class MapControllerClass {
  constructor(MapData, MapView) {
    this.MD = MapData
    this.MV = MapView
  }

  requestNearbyUsers = async (lonlat, currentUser, options={ removeOldUsersImmediately: true, preloadedUsers: [] }) => {
    const { MD, MV, nearbyUsersPromise, handleReturnedUsers } = this
    console.log('GETTING MAP DATA')
    // Remove users in cases where we are re-requesting data
    if (options?.removeOldUsersImmediately) MV.removeAllUsers()

    // Check if users are preloaded
    if (Array.isArray(options?.preloadedUsers) && options.preloadedUsers.length > 0) {
      // If preloaded users are passed, go straight to the handler
      handleReturnedUsers(options.preloadedUsers, currentUser, lonlat, options)
      return Promise.resolve(options.preloadedUsers)
    } else {
      // If users aren't preloaded, request them now and pass to handler
      return nearbyUsersPromise(lonlat).then((res) => {
        handleReturnedUsers(res.data, currentUser, lonlat, options)
      })
    }

  }

  nearbyUsersPromise = async (lonlat) => {
    const [longitude, latitude] = lonlat
    const url = `/api/users/nearby_profiles?radius=true&lonlat=[${longitude},${latitude}]`
    return axios.get(url)
  }

  handleReturnedUsers = (userData, currentUser, lonlat, options) => {
    const { MD, MV, initializeMapEventHandlers, prepareUserData, renderChanges } = this
    if (!options?.removeOldUsersImmediately) MV.removeAllUsers()
    // Go through recently created nodes and append html profile markers
    prepareUserData(lonlat, userData, currentUser).forEach((user) => {
      new MD.ProfileMarker({ MD, user }).addToD3LoweredOrRaised()
      MD.setProfile(user)
    })

    // Update AnimatedLonlatDatum after setting new D3 datum
    MD.updateAnimatedLonlatDatumToBaseVal()

    // Initial render for force simulation to move nearby users away on screen load
    renderChanges()

    // Event handlers
    initializeMapEventHandlers()
  }

  prepareUserData = (lonlat, data, currentUser) => {
    // Shuffle the user data so the profiles don't render in an odd tree-like fashion
    const users = shuffleArr(data.filter((u) => !!u.lonlat))
    // NOTE: You aren't sure how you feel about focusing the user on the first render. It might be too much, but I want more opinions
    // const currentData = { ...currentUser, lonlat, isCurrentUser: true, isFocused: true }
    const currentData = { ...currentUser, lonlat, isCurrentUser: true }
    // Add current user
    users.push(currentData)
    // Set current user to the initial focused user
    // this.MD.setFocusedUser(currentData)
    return users
  }

  initializeMapEventHandlers = () => {
    const { MD, renderChanges } = this
    if (MD.mapEventHandlesInitialized) return;
    MD.map.on("viewreset", () => renderChanges())
    MD.map.on("move", () => renderChanges())
    MD.map.on('zoom', () => {
      const currentZoom = MD.map.getZoom()
      if (currentZoom > MD.lastZoom.value) {
        // Zooming in
        // Update AnimatedLonlatDatum values closer to their original location
        MD.AnimatedLonlatDatum = MD.AnimatedLonlatDatum.map(({ id, lonlat }) => {
          // lonlat format: { lng: -111.86033895977853, lat: 40.759764503363925 }
          const user = MD.getProfile(id)
          if (!user) return new MD.LonlatDatum({ id, lonlat });
          const [lng, lat] = MD.movePointCloser([lonlat.lng, lonlat.lat], user.lonlat)
          return new MD.LonlatDatum({ lonlat: { lng, lat }, id })
        })
      }
      MD.lastZoom.value = currentZoom;
      renderChanges()
    })
    MD.setMapEventHandlesInitialized(true)
  }

  renderChanges = () => {
    const { MD, ticked, performForceSimulation } = this

    // Project the lonlat datum to X & Y after map event
    const projectedDatum = MD.AnimatedLonlatDatum.map((datum) => new MD.XYDatum({
      ...MD.map.project(datum.lonlat), // X & Y
      id: datum.id,
    }))

    // set nodes with current data as calculated above
    MD.D3Selection.data(projectedDatum)

    // update x & y attributes with the data passed
    ticked()
    
    // Perform the (throttled) force simulation
    performForceSimulation(projectedDatum)
  }

  performForceSimulation = (projectedDatum) => {
    const { MD, ticked } = this
    // The timeout is a method of debouncing, so the animation only starts once 100ms have gone by without the map re-rendering
    clearTimeout(MD.forceAnimationTimeout.value)
    MD.forceAnimationTimeout.value = setTimeout(() => {
    // Only perform animation if the zoom is less than 11
      if (MD.lastZoom.value < 12) return;
      // Run new d3 simulation on newly projected data
      // NOTE: You tried "restarting" a saved simulation, but it would ocasionally fail to restart as expected after travel mode
      const simulation = d3
        .forceSimulation(projectedDatum)
        .force("collide", d3.forceCollide().radius(d => d.r + 1).strength(0))
        .on("tick", ticked)
      // The tick event is dispatched by the force simulation at the end of each simulation step
      // Our ticked callback updates the x & y positions in the d3 dot group to the new values of the datum.

      // Ramp up collision strength to provide smooth transition
      let duration = 500 // total duration of the animation
      let timer = d3.timer(elapsed => {
        let dt = elapsed / duration
        simulation.force("collide").strength(dt)
        if (dt >= 1.0) {
          timer.stop()
          simulation.stop()
        }
      })
    }, 100) // 100ms is the debounced duration
  }

  // Function that updates all d3 x & y values to the datums value, then updates the AnimatedLonlatDatum accordingly
  // NOTE: the simulation tick event is fired on every render and when the simulation occurs
  ticked = () => {
    const { MD } = this
    // This sets the x & y positions of the markers on the map
    // to the new x/y values of the updated datum
    MD.updateD3PositionsToDatum()

    // Once the datum x & y is updated, we need to unproject that
    // back to lonlat values so we can re-run the render loop
    const currentZoom = MD.map.getZoom();
    if (currentZoom <= MD.lastZoom.value) {
      MD.updateAnimatedLonlatDatumToAnimVal()
    }
  }

  // Handler for page event where WebGL loses it's context.
  // Browsers will eventually remove resources from unused tabs so there isn't too much running in the background,
  // and this includes WebGL which runs the canvas our map is using. Once the broswer unallocates the resources for our map,
  // it goes blank, and we either need to re-retrieve the map or just hard reload the page.
  handleMapWebGLContextLost = () => {
    alert('webglcontextlost')
    console.log('A webglcontextlost event occurred.');
    window?.location?.reload?.()
  }
}



// *******************************
// * MAP MVC CLASS INSTANTIATION *
// *******************************
const MapData = new MapDataClass()
const MapView = new MapViewClass(MapData)
const MapController = new MapControllerClass(MapData, MapView)



// ***************
// * MAP CONTEXT *
// ***************
const MapDataContext = createContext()
const MapDataProvider = ({ children }) => {
  const { state: authState, authStateRef, authenticated, initialAuthentication, PENDING_INITIAL_AUTH } = useAuthContext()
  const { isLoading: isProfilesReloading, isLoadingDelayed: isProfilesReloadingDelayed, setIsLoading: setIsProfilesReloading } = useLoading()
  const { currentLocation, currentLocationRef } = useCurrentLocationContext()
  const [mapContainer, setMapContainer, mapContainerRef] = useStateRef(null)
  const [isWebsocketConnected, setIsWebsocketConnected] = useState(false)
  const [map, setMap, mapRef] = useStateRef(null)
  const [mapDataIsSet, setMapDataIsSet] = useState(false)
  const [navigateFuncIsSet, setNavigateFuncIsSet] = useState(false)
  const mapDataIsInitialized = mapDataIsSet && navigateFuncIsSet
  const [profilesInitialized, setProfilesInitialized] = useState(false)
  const [isRequestingUsers, setIsRequestingUsers] = useState(false)
  const [preloadedUsers, setPreloadedUsers] = useState([])
  const [isUsersPreloaded, setIsUsersPreloaded] = useState(false)
  // const [exitAppPromptIsSet, setExitAppPromptIsSet] = useState(false)
  const location = useLocation()
  // const navigate = useNavigate()
  const userOnMapScreen = authenticated && (location.pathname === '/' || location.pathname === '/map')

  // Set the map for map MVC classes internal usage
  useLayoutEffect(() => {
    if (mapDataIsSet || map) return;
    if (!mapContainer || !authState.user || !currentLocation) return;

    mapContainer.style.visibility = 'hidden'
    // Setting the map
    setMap(MapData.initializeMap(currentLocation, mapContainer))
    
    // The SVG is the d3 container for all the foreignObjects
    MapData.initializeD3Container()
    MapData.updateD3Selection()
    MapData.setMapDataIsInitialized(true)
    setMapDataIsSet(true)
  }, [map, setMap, currentLocation, authState.user, mapContainer, mapDataIsSet])

  // Request users once you have the current location
  useLayoutEffect(() => {
    if (isRequestingUsers || isUsersPreloaded) return;
    if (!authState.user || !currentLocation) return;
    setIsRequestingUsers(true)
    MapController.nearbyUsersPromise(currentLocation)
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        setPreloadedUsers(res.data)
        setIsUsersPreloaded(true)
      })
      .finally(() => setIsRequestingUsers(false))
  }, [userOnMapScreen, authState, currentLocation, map, profilesInitialized, mapDataIsInitialized, mapContainer])
  
  // Set nearby profiles on the map once we have both the map initialized and the users preloaded
  useLayoutEffect(() => {
    if (profilesInitialized || isRequestingUsers || !isUsersPreloaded) return;
    if (!mapDataIsInitialized || !mapContainer || !map || !authState.user || !currentLocation) return;
    MapController
      .requestNearbyUsers(currentLocation, authState?.user, { preloadedUsers })
      .finally(() => {
        MapData.setProfilesInitialized(true)
        setProfilesInitialized(true)
        setPreloadedUsers([])
      })
  }, [authState, currentLocation, map, profilesInitialized, mapDataIsInitialized, mapContainer, preloadedUsers, isUsersPreloaded])

  // Makes map visible when profiles are initialized
  useLayoutEffect(() => {
    if (profilesInitialized) {
      setTrueMapContainerVisible()
    }
  }, [profilesInitialized])

  // TODO: Sets the current location when it updates from the current user moving (to be tested)
  useLayoutEffect(() => {
    if (!authState?.user?.nearbyUuid || !MapData.getProfile(authState?.user?.nearbyUuid) || !currentLocation) return;
    MapView.updateUser(authState?.user?.nearbyUuid, { lonlat: currentLocation })
  }, [authState?.user?.nearbyUuid, currentLocation])

  // Handles scenario where the page loses its webgl context, thus turning the map canvas blank because it's resources were unallocated
  useLayoutEffect(() => {
    if (!map) return;
    map.on('webglcontextlost', MapController.handleMapWebGLContextLost);
    return () => map.off('webglcontextlost', MapController.handleMapWebGLContextLost)
  }, [map, MapController])

  // Sets the exit app modal to appear before the user leaves the application
  // useLayoutEffect(() => {
  //   if (!exitAppPromptIsSet) {
  //     navigate('/?action_modal=exit-app', { replace: true })
  //     navigate('/')
  //     setExitAppPromptIsSet(true)
  //   }
  // }, [exitAppPromptIsSet])

  const nearbyProfileRefreshRequest = async ({ removeOldUsersImmediately=true }) => {
    if (!currentLocationRef.current) {
      console.log('CURRENT LOCATION NOT SET')
      return;
    } else {
      setIsProfilesReloading(true)
      return MapController.requestNearbyUsers(currentLocationRef.current, authStateRef?.current?.user || authState?.user, { removeOldUsersImmediately })
        .catch(console.error)
        .finally(() => setIsProfilesReloading(false))
    }
  }

  const setTrueMapContainerVisible = () => {
    const trueMapContainer = mapRef?.current?.getContainer?.()
    if (trueMapContainer) trueMapContainer.style.visibility = 'visible'
  }

  return (
    <MapDataContext.Provider value={{
      map,
      setMap,
      mapRef,
      mapContainer,
      setMapContainer,
      mapContainerRef,
      userOnMapScreen,
      mapDataIsSet,
      navigateFuncIsSet,
      setNavigateFuncIsSet,
      mapDataIsInitialized,
      profilesInitialized,
      setIsWebsocketConnected,
      isWebsocketConnected,
      isProfilesReloading,
      isProfilesReloadingDelayed,
      setIsProfilesReloading,
      nearbyProfileRefreshRequest,
      setTrueMapContainerVisible,
    }}>
      {children}
    </MapDataContext.Provider>
  )
}

const useMapDataContext = () => useContext(MapDataContext)

export default useMapDataContext

export { MapDataContext, MapDataProvider, useMapDataContext, MapData, MapView, MapController }
